import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useWebSocketStore } from './websocketStore';
import type { 
  MonitoringResult, 
  CalibrationInfo, 
  AlertLevel, 
  GazeData, 
  BaselineStats
} from '@/types';
import { normalizeBehavioralFlags } from '@/utils/proctorFlags';
import { shouldSurfaceMonitoringError } from '@/monitoring/studentReadiness';

interface MonitoringState {
  isConnected: boolean;
  isCalibrating: boolean;
  currentData: MonitoringResult | null;
  calibrationInfo: CalibrationInfo | null;
  alertHistory: MonitoringResult[];
  gazeHistory: GazeData[];
  /** Latest ETH-XGaze annotated JPEG (base64, no data-URI prefix) */
  processedFrame: string | null;
  systemUsed: string | null;
  sessionStats: {
    totalAlerts: number;
    alertsByLevel: Record<AlertLevel, number>;
    averageFocusPercentage: number;
    sessionDuration: number;
    startTime: number | null;
  };
  baseline: BaselineStats | null;
  isMonitoringActive: boolean;
  error: string | null;
  lastUpdate: number | null;
}

interface MonitoringActions {
  connect: (sessionId: string) => void;
  disconnect: () => void;
  startMonitoring: () => void;
  stopMonitoring: () => void;
  addMonitoringData: (data: MonitoringResult) => void;
  updateCalibration: (info: CalibrationInfo) => void;
  clearHistory: () => void;
  clearAlertHistory: () => void;
  getAlertSummary: () => { total: number; byLevel: Record<AlertLevel, number> };
  getSessionDuration: () => number;
  getFocusPercentage: () => number;
  setError: (error: string | null) => void;
  resetSession: () => void;
  exportData: () => any;
  updateBaseline: (baseline: BaselineStats) => void;
}

type MonitoringStore = MonitoringState & MonitoringActions;

const MAX_HISTORY_SIZE = 1000;
const MAX_GAZE_HISTORY_SIZE = 500;

const useMonitoringStoreBase = create<MonitoringStore>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        isConnected: false,
        isCalibrating: false,
        currentData: null,
        calibrationInfo: null,
        alertHistory: [],
        gazeHistory: [],
        processedFrame: null,
        systemUsed: null,
        sessionStats: {
          totalAlerts: 0,
          alertsByLevel: {
            NORMAL: 0,
            DISTRACTED: 0,
            WARNING: 0,
            CRITICAL_ALERT: 0,
          },
          averageFocusPercentage: 0,
          sessionDuration: 0,
          startTime: null,
        },
        baseline: null,
        isMonitoringActive: false,
        error: null,
        lastUpdate: null,

        // Actions
        connect: (_sessionId: string) => {
          const wsStore = useWebSocketStore.getState();
          
          // Subscribe to monitoring data
          wsStore.subscribe('monitoring_result', (data: MonitoringResult) => {
            get().addMonitoringData(data);
          });
          
          // Subscribe to calibration updates
          wsStore.subscribe('calibration_update', (data: CalibrationInfo) => {
            const normalized = normalizeCalibrationInfo(data);
            if (normalized) {
              get().updateCalibration(normalized);
            }
          });

          // Backend errors — ignore expected webgazer-mode ETH notices
          wsStore.subscribe('error', (data: { message?: string } | string) => {
            const message =
              typeof data === 'string'
                ? data
                : data?.message || 'Monitoring error';
            if (!shouldSurfaceMonitoringError(message)) {
              return;
            }
            get().setError(message);
          });
          
          set({ 
            isConnected: true,
            error: null,
            sessionStats: {
              ...get().sessionStats,
              startTime: Date.now(),
            },
          });
        },

        disconnect: () => {
          set({
            isConnected: false,
            isMonitoringActive: false,
            currentData: null,
            processedFrame: null,
            systemUsed: null,
          });
        },

        startMonitoring: () => {
          set({
            isMonitoringActive: true,
            sessionStats: {
              ...get().sessionStats,
              startTime: get().sessionStats.startTime || Date.now(),
            },
          });
        },

        stopMonitoring: () => {
          set({ isMonitoringActive: false });
        },

        addMonitoringData: (data: MonitoringResult) => {
          const currentState = get();
          const wire = data as MonitoringResult & {
            calibration_info?: unknown;
            alert_level?: AlertLevel;
            focus_percentage?: number;
            confidence_score?: number;
            adaptive_output?: {
              alert_level?: AlertLevel;
              focus_percentage?: number;
              confidence_score?: number;
            };
            gaze_data?: GazeData;
            processed_frame?: string;
            system_used?: string;
            behavioral_flags?: Record<string, unknown>;
          };

          // Backend nests calibration under calibration_info (snake_case).
          // Normalize into the store so UI can show real progress.
          const rawCalibration = wire.calibrationInfo ?? wire.calibration_info;
          const normalizedCalibration = normalizeCalibrationInfo(rawCalibration);
          if (normalizedCalibration) {
            get().updateCalibration(normalizedCalibration);
          }

          const adaptive = wire.adaptive_output;
          const processedFrame =
            (typeof data.processedFrame === 'string' && data.processedFrame) ||
            (typeof wire.processed_frame === 'string' && wire.processed_frame) ||
            null;
          const systemUsed =
            data.systemUsed ??
            wire.system_used ??
            null;

          const behavioralFlags = normalizeBehavioralFlags(
            data.behavioralFlags ?? wire.behavioral_flags
          );

          const normalizedData: MonitoringResult = {
            ...data,
            alertLevel:
              data.alertLevel ??
              wire.alert_level ??
              adaptive?.alert_level ??
              'NORMAL',
            focusPercentage:
              data.focusPercentage ??
              wire.focus_percentage ??
              adaptive?.focus_percentage ??
              0,
            confidenceScore:
              data.confidenceScore ??
              wire.confidence_score ??
              adaptive?.confidence_score ??
              0,
            gazeData: data.gazeData ?? wire.gaze_data,
            calibrationInfo: normalizedCalibration ?? data.calibrationInfo,
            behavioralFlags,
            processedFrame: processedFrame || undefined,
            systemUsed: systemUsed || undefined,
          };
          
          // Update current data + annotated frame for the live preview
          set({
            currentData: normalizedData,
            lastUpdate: Date.now(),
            error: null,
            ...(processedFrame ? { processedFrame } : {}),
            ...(systemUsed ? { systemUsed } : {}),
          });
          
          // Add to alert history if it's an alert
          if (normalizedData.alertLevel !== 'NORMAL') {
            const newAlertHistory = [...currentState.alertHistory, normalizedData];
            
            // Limit history size
            if (newAlertHistory.length > MAX_HISTORY_SIZE) {
              newAlertHistory.splice(0, newAlertHistory.length - MAX_HISTORY_SIZE);
            }
            
            // Update session stats
            const updatedStats = {
              ...currentState.sessionStats,
              totalAlerts: currentState.sessionStats.totalAlerts + 1,
              alertsByLevel: {
                ...currentState.sessionStats.alertsByLevel,
                [normalizedData.alertLevel]: currentState.sessionStats.alertsByLevel[normalizedData.alertLevel] + 1,
              },
            };
            
            set({
              alertHistory: newAlertHistory,
              sessionStats: updatedStats,
            });
          }
          
          // Add gaze data to history if available
          if (normalizedData.gazeData) {
            const newGazeHistory = [...currentState.gazeHistory, normalizedData.gazeData];
            
            // Limit gaze history size
            if (newGazeHistory.length > MAX_GAZE_HISTORY_SIZE) {
              newGazeHistory.splice(0, newGazeHistory.length - MAX_GAZE_HISTORY_SIZE);
            }
            
            set({ gazeHistory: newGazeHistory });
          }
          
          // Update average focus percentage
          const totalFocusSum = currentState.alertHistory.reduce((sum, alert) => sum + alert.focusPercentage, 0) + normalizedData.focusPercentage;
          const totalDataPoints = currentState.alertHistory.length + 1;
          
          set({
            sessionStats: {
              ...get().sessionStats,
              averageFocusPercentage: Math.round(totalFocusSum / totalDataPoints),
            },
          });
        },

        updateCalibration: (info: CalibrationInfo) => {
          set({
            calibrationInfo: info,
            isCalibrating: info.status === 'calibrating',
          });
        },

        clearHistory: () => {
          set({
            alertHistory: [],
            gazeHistory: [],
            sessionStats: {
              totalAlerts: 0,
              alertsByLevel: {
                NORMAL: 0,
                DISTRACTED: 0,
                WARNING: 0,
                CRITICAL_ALERT: 0,
              },
              averageFocusPercentage: 0,
              sessionDuration: 0,
              startTime: Date.now(),
            },
          });
        },

        clearAlertHistory: () => {
          set({
            alertHistory: [],
            sessionStats: {
              ...get().sessionStats,
              totalAlerts: 0,
              alertsByLevel: {
                NORMAL: 0,
                DISTRACTED: 0,
                WARNING: 0,
                CRITICAL_ALERT: 0,
              },
            },
          });
        },

        getAlertSummary: () => {
          const { sessionStats } = get();
          return {
            total: sessionStats.totalAlerts,
            byLevel: sessionStats.alertsByLevel,
          };
        },

        getSessionDuration: () => {
          const { sessionStats } = get();
          if (!sessionStats.startTime) return 0;
          return Math.floor((Date.now() - sessionStats.startTime) / 1000);
        },

        getFocusPercentage: () => {
          const { sessionStats } = get();
          return sessionStats.averageFocusPercentage;
        },

        setError: (error: string | null) => {
          set({ error });
        },

        resetSession: () => {
          set({
            currentData: null,
            calibrationInfo: null,
            alertHistory: [],
            gazeHistory: [],
            processedFrame: null,
            systemUsed: null,
            sessionStats: {
              totalAlerts: 0,
              alertsByLevel: {
                NORMAL: 0,
                DISTRACTED: 0,
                WARNING: 0,
                CRITICAL_ALERT: 0,
              },
              averageFocusPercentage: 0,
              sessionDuration: 0,
              startTime: null,
            },
            isCalibrating: false,
            isMonitoringActive: false,
            error: null,
            lastUpdate: null,
          });
        },

        exportData: () => {
          const state = get();
          return {
            sessionStats: state.sessionStats,
            alertHistory: state.alertHistory,
            gazeHistory: state.gazeHistory,
            calibrationInfo: state.calibrationInfo,
            baseline: state.baseline,
            sessionDuration: get().getSessionDuration(),
            exportedAt: new Date().toISOString(),
          };
        },

        updateBaseline: (baseline: BaselineStats) => {
          set({ baseline });
        },

      }),
      {
        name: 'quizsecure-monitoring',
        partialize: (state) => ({
          // Only persist essential data, not real-time data
          baseline: state.baseline,
          sessionStats: state.sessionStats,
        }),
      }
    ),
    { name: 'MonitoringStore' }
  )
);

// Enhanced hook with computed properties
export const useMonitoringStore = () => {
  const store = useMonitoringStoreBase();
  
  return {
    ...store,
    // Computed properties
    isMonitoring: store.isMonitoringActive,
    currentBehavior: store.currentData?.alertLevel || 'NORMAL',
    focusLevel: store.sessionStats.averageFocusPercentage,
    alertCount: store.sessionStats.totalAlerts,
    sessionDuration: store.getSessionDuration(),
    gazeData: store.gazeHistory.length > 0 ? store.gazeHistory[store.gazeHistory.length - 1] : null,
    isCalibrated: store.calibrationInfo?.status === 'completed' || false,
    calibrationProgress: store.calibrationInfo?.progress || 0,
  };
};

// Utility functions for alert analysis
export const getAlertSeverity = (alertLevel: AlertLevel): number => {
  const severityMap = {
    NORMAL: 0,
    DISTRACTED: 1,
    WARNING: 2,
    CRITICAL_ALERT: 3,
  };
  return severityMap[alertLevel] || 0;
};

export const getAlertColor = (alertLevel: AlertLevel): string => {
  const colorMap = {
    NORMAL: 'text-state-nominal bg-state-nominal-soft',
    DISTRACTED: 'text-state-warning bg-state-warning-soft',
    WARNING: 'text-state-warning bg-state-warning-soft',
    CRITICAL_ALERT: 'text-state-critical bg-state-critical-soft',
  };
  return colorMap[alertLevel] || 'text-content-secondary bg-surface-nested';
};

// Real-time monitoring analysis
export const analyzeMonitoringTrends = (history: MonitoringResult[], timeWindow: number = 60000) => {
  const now = Date.now();
  const recentAlerts = history.filter(alert => now - alert.timestamp < timeWindow);
  
  if (recentAlerts.length === 0) {
    return {
      trend: 'stable',
      avgConfidence: 0,
      avgFocus: 0,
      alertFrequency: 0,
    };
  }
  
  const avgConfidence = recentAlerts.reduce((sum, alert) => sum + alert.confidenceScore, 0) / recentAlerts.length;
  const avgFocus = recentAlerts.reduce((sum, alert) => sum + alert.focusPercentage, 0) / recentAlerts.length;
  const alertFrequency = recentAlerts.length / (timeWindow / 60000); // alerts per minute
  
  // Determine trend
  let trend = 'stable';
  if (recentAlerts.length >= 3) {
    const recent = recentAlerts.slice(-3);
    const focusTrend = recent[2].focusPercentage - recent[0].focusPercentage;
    
    if (focusTrend > 10) trend = 'improving';
    else if (focusTrend < -10) trend = 'declining';
  }
  
  return {
    trend,
    avgConfidence: Math.round(avgConfidence),
    avgFocus: Math.round(avgFocus),
    alertFrequency: Math.round(alertFrequency * 100) / 100,
  };
};

/** Normalize backend calibration payloads (snake_case, 0–1 progress) for the UI. */
export const normalizeCalibrationInfo = (raw: unknown): CalibrationInfo | null => {
  if (!raw || typeof raw !== 'object') return null;

  const info = raw as Record<string, unknown>;
  // Backend may briefly emit insufficient_data; keep the modal progressing.
  const status =
    info.status === 'insufficient_data' ? 'calibrating' : info.status;
  if (
    status !== 'not_started' &&
    status !== 'calibrating' &&
    status !== 'completed'
  ) {
    return null;
  }

  const rawProgress = Number(info.progress ?? 0);
  // Backend AdaptiveBaseline reports progress as 0–1; treat values ≤1 as fractions.
  const progressPercent =
    status === 'completed'
      ? 100
      : rawProgress > 0 && rawProgress <= 1
        ? Math.round(rawProgress * 100)
        : Math.round(Math.min(100, Math.max(0, rawProgress)));

  const timeRemaining = info.timeRemaining ?? info.time_remaining;
  const samplesCollected = info.samplesCollected ?? info.samples_collected;
  const canStartExam = info.canStartExam ?? info.can_start_exam;

  return {
    status,
    progress: progressPercent,
    timeRemaining: timeRemaining == null ? undefined : Number(timeRemaining),
    samplesCollected: samplesCollected == null ? undefined : Number(samplesCollected),
    canStartExam: canStartExam == null ? undefined : Boolean(canStartExam),
    message: typeof info.message === 'string' ? info.message : undefined,
  };
};

// Calibration progress tracking (always 0–100 for Progress components)
export const getCalibrationProgress = (calibrationInfo: CalibrationInfo | null): number => {
  if (!calibrationInfo) return 0;

  switch (calibrationInfo.status) {
    case 'not_started':
      return 0;
    case 'calibrating':
      return calibrationInfo.progress ?? 0;
    case 'completed':
      return 100;
    default:
      return 0;
  }
};